import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Zap } from 'lucide-react';
import { MethodEditorDrawer } from './method-editor-drawer';
import type { MethodFormData } from '@/types/integration-form-types';

const VERB_COLORS: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-green-100 text-green-700',
    PUT: 'bg-amber-100 text-amber-700',
    PATCH: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
};

interface WizardStepMethodsProps {
    value: MethodFormData[];
    onChange: (methods: MethodFormData[]) => void;
    appKey?: string;
    currentUserId?: string;
    currentOrgId?: string;
}

export function WizardStepMethods({ value, onChange, appKey, currentUserId, currentOrgId }: WizardStepMethodsProps) {
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const handleAdd = () => {
        setEditingIndex(null);
        setEditorOpen(true);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setEditorOpen(true);
    };

    const handleDelete = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleSave = (method: MethodFormData) => {
        if (editingIndex !== null) {
            const updated = value.map((m, i) => (i === editingIndex ? method : m));
            onChange(updated);
        } else {
            onChange([...value, method]);
        }
    };

    const existingNames = value
        .filter((_, i) => i !== editingIndex)
        .map((m) => m.name);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Zap size={16} className="text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">
                        Methods ({value.length})
                    </span>
                </div>
                <Button type="button" size="sm" onClick={handleAdd} className="gap-1">
                    <Plus size={14} />
                    Add Method
                </Button>
            </div>

            {value.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-slate-200 px-8 py-6 text-center">
                    <Zap className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-600">No methods configured</p>
                    <p className="text-xs text-slate-400 mt-1">
                        Add API methods that map to capability endpoints.
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAdd}
                        className="mt-3 gap-1"
                    >
                        <Plus size={14} />
                        Add First Method
                    </Button>
                </div>
            ) : (
                <div className="rounded-md border border-slate-200 divide-y divide-slate-200">
                    {value.map((method, index) => (
                        <div
                            key={method.name}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                        >
                            <Badge
                                variant="secondary"
                                className={`text-[10px] font-mono w-16 justify-center shrink-0 ${VERB_COLORS[method.verb] ?? ''}`}
                            >
                                {method.verb}
                            </Badge>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-mono font-medium text-slate-800 truncate">
                                    {method.name}
                                </p>
                                <p className="text-xs text-slate-400 font-mono truncate">
                                    {method.path}
                                </p>
                            </div>
                            {method.description && (
                                <p className="text-xs text-slate-500 max-w-[300px] truncate">
                                    {method.description}
                                </p>
                            )}
                            <div className="flex items-center gap-1 shrink-0">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(index)}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700"
                                >
                                    <Pencil size={14} />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(index)}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                                >
                                    <Trash2 size={14} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <MethodEditorDrawer
                open={editorOpen}
                onOpenChange={setEditorOpen}
                method={editingIndex !== null ? value[editingIndex] : undefined}
                existingNames={existingNames}
                onSave={handleSave}
                appKey={appKey}
                currentUserId={currentUserId}
                currentOrgId={currentOrgId}
            />
        </div>
    );
}
